// frontend/src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Timestamp } from 'firebase/firestore';

// 🔹 Serviços de dashboard
import {
  contarAnunciosAtivos,
  contarImoveisComPlaca,
  contarNegociosPorStatus,
  listarVendasPorMes,
  listarVendasRecentes,
  contarVisitasRealizadas,
  buildPeriod,
  listarAnosDisponiveisVendas,
  listarMovimentoPorMes,
} from '../services/dashboardService';

// ✅ SCORE (LeadershipEngine)
import { getScoreSnapshot } from '../services/leadershipEngine/scoreRepository';
import { getNivelByRenda12m } from '../services/leadershipEngine/scoreLevels.config';

// ✅ Componentes
import HeroResultado from '../components/dashboard/HeroResultado';
import InsightPanel from '../components/dashboard/InsightPanel';
import IndicadorCard from '../components/dashboard/IndicadorCard';
import FunilDeVendas from '../components/dashboard/FunilDeVendas';
import GraficoFinanceiro12m from '../components/dashboard/GraficoFinanceiro12m';
import GraficoMovimento12m from '../components/dashboard/GraficoMovimento12m';
import TabelaVendas from '../components/dashboard/TabelaVendas';

// ✅ Score Radial (multi-ring)
import ScoreRadialPerformance from '../components/dashboard/leadership/ScoreRadialPerformance';

// Ícones (lucide)
import {
  BarChart2,
  MapPin,
  Briefcase,
  CalendarCheck,
  CheckCircle2,
  Plus,
} from 'lucide-react';

/* ---------- Flags ---------- */
const SHOW_DIAGNOSTICS =
  (typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SHOW_DASH_DIAGNOSTICS === 'true') ||
  false;

/* ---------- ErrorBoundary + SafeBlock ---------- */
class ErrorBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    if (import.meta.env.MODE !== 'production') {
      console.error(`[ErrorBoundary:${this.props.label}]`, error, info);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-2xl border border-red-200/30 bg-red-500/10 p-4 text-sm text-red-200">
          <div className="mb-1 font-semibold">
            Erro ao renderizar: {this.props.label}
          </div>
          <div className="break-all opacity-80">
            {String(this.state.error?.message || this.state.error || 'Erro')}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function isProbablyComponent(Comp) {
  return (
    Comp &&
    (typeof Comp === 'function' ||
      (typeof Comp === 'object' && (Comp.$$typeof || Comp.render || Comp)))
  );
}

function SafeBlock({ label, Comp, children, ...props }) {
  if (!isProbablyComponent(Comp)) {
    return (
      <div className="rounded-2xl border border-amber-200/30 bg-amber-500/10 p-4 text-sm text-amber-200">
        <div className="mb-1 font-semibold">Componente inválido: {label}</div>
        <div className="opacity-80">
          Recebi <code>{String(typeof Comp)}</code>. Verifique o{' '}
          <strong>export default</strong> de <code>{label}</code>.
        </div>
      </div>
    );
  }
  return (
    <ErrorBoundary label={label}>
      {React.createElement(Comp, props, children)}
    </ErrorBoundary>
  );
}

/* ---------- utils ---------- */
function numberOr0(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function clamp(n, min, max) {
  const N = Number(n || 0);
  return Math.max(min, Math.min(max, N));
}

function isCurrentYear(y) {
  return Number(y) === new Date().getFullYear();
}

/* =========================================================
   ✅ Períodos ANCORADOS no ANO selecionado
========================================================= */
function makeYearBounds(year) {
  const y = Number(year);
  const safeYear = Number.isFinite(y) ? y : new Date().getFullYear();

  const start = new Date(safeYear, 0, 1, 0, 0, 0, 0);
  const end = isCurrentYear(safeYear)
    ? new Date()
    : new Date(safeYear, 11, 31, 23, 59, 59, 999);

  return { start, end };
}

function makeYearPeriod(year) {
  const { start, end } = makeYearBounds(year);
  return { de: Timestamp.fromDate(start), ate: Timestamp.fromDate(end) };
}

function buildLastMonthsWithinYear(year, monthsBack = 3) {
  const { start: yearStart, end: yearEnd } = makeYearBounds(year);

  const end = yearEnd;
  const start = new Date(end);
  start.setMonth(start.getMonth() - Number(monthsBack || 0));
  start.setHours(0, 0, 0, 0);

  if (start < yearStart) start.setTime(yearStart.getTime());

  return { de: Timestamp.fromDate(start), ate: Timestamp.fromDate(end) };
}

function makePeriodFromFunnel(mode, year) {
  if (mode === 'semestral') return buildLastMonthsWithinYear(year, 6);
  if (mode === 'anual') return makeYearPeriod(year);
  return buildLastMonthsWithinYear(year, 3); // trimestral
}

function labelPeriodo(mode) {
  if (mode === 'semestral') return 'Semestral';
  if (mode === 'anual') return 'Anual';
  return 'Trimestral';
}

/* =========================================================
   ✅ Score SEMPRE TOTAL (janela fixa 12m)
========================================================= */
function makeScoreWindowPeriod12m() {
  return buildPeriod({ mode: 'last12' });
}

/* =========================================================
   ✅ KPIs topo: helpers (TOTAL + MÊS + Delta)
========================================================= */
function pickMonthIndexForYear(year, monthlyArrayLen = 12) {
  const now = new Date();
  const isCurrent = Number(year) === now.getFullYear();
  const idx = isCurrent ? now.getMonth() : monthlyArrayLen - 1; // Dez
  return Math.max(0, Math.min(monthlyArrayLen - 1, idx));
}

function seriesFromMov(mov, key) {
  const arr = Array.isArray(mov) ? mov : [];
  const out = new Array(12).fill(0);
  for (let i = 0; i < Math.min(12, arr.length); i += 1) {
    out[i] = Number(arr[i]?.[key] || 0);
  }
  return out;
}

function sumSeries(arr) {
  return (arr || []).reduce((acc, v) => acc + (Number(v) || 0), 0);
}

function deltaVsPrev(series, idx) {
  if (!Array.isArray(series) || !series.length) return 0;
  const cur = Number(series[idx] || 0);
  const prev = idx > 0 ? Number(series[idx - 1] || 0) : 0;
  return cur - prev;
}

/* ---------- Header premium (dark) ---------- */
function DashboardHeader({
  subtitle = 'Bem-vindo de volta.',
  yearSlot,
  onNovaVenda,
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-white">
          Painel de Performance
        </h1>
        <p className="mt-1 text-base text-white/70">{subtitle}</p>
      </div>

      <div className="flex items-center gap-2">
        {yearSlot}

        <button
          type="button"
          onClick={onNovaVenda}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-3 py-2 text-xs font-bold text-[#06131f] hover:bg-emerald-400"
        >
          <Plus size={16} />
          Nova venda
        </button>
      </div>
    </div>
  );
}

/* ---------- COMPONENTE PRINCIPAL ---------- */
export default function Dashboard() {
  const { usuario } = useAuth() || {};

  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(null);

  // ✅ SCORE state
  const [scoreLoading, setScoreLoading] = useState(true);
  const [scoreError, setScoreError] = useState(null);
  const [scoreData, setScoreData] = useState(null);

  // ✅ Período do FUNIL (trimestral | semestral | anual)
  const [periodMode, setPeriodMode] = useState('trimestral');

  // ✅ ANO da dashboard
  const [yearsLoading, setYearsLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [dashboardYear, setDashboardYear] = useState(new Date().getFullYear());

  // ✅ Loading do ANO (financeiro/movimento/tabela)
  const [yearLoading, setYearLoading] = useState(true);

  const [dashData, setDashData] = useState({
    anunciosAtivos: 0,
    imoveisComPlaca: 0,
    totalNegocios: 0, // ALL TIME

    negociosPorStatusPeriodo: {},

    // ano selecionado
    visitasAno: 0,
    contratosAno: 0,
    vgvAno: 0,
    honorariosAno: 0,
    faturamentoAno: 0,
    tendenciaVgv: null,
    vendasMensalAno: [],
    vendasRecentesAno: [],
    honorariosMeses: [],

    movimentoMensalAno: [],

    // score rings (janela fixa 12m)
    score12mNegociosTotal: 0,
    score12mVisitas: 0,
    score12mFechamentos: 0,
  });

  /* =========================================================
     ✅ Carrega lista de anos disponíveis (base: vendas)
  ========================================================= */
  useEffect(() => {
    let ativo = true;

    async function carregarAnos() {
      if (!usuario?.uid) return;

      setYearsLoading(true);
      try {
        const ys = await listarAnosDisponiveisVendas(usuario.uid);
        if (!ativo) return;

        const list = Array.isArray(ys) ? ys : [];
        const currentYear = new Date().getFullYear();

        const merged = Array.from(new Set([...list, currentYear])).sort(
          (a, b) => a - b
        );

        setYears(merged);

        if (merged.includes(currentYear)) setDashboardYear(currentYear);
        else if (merged.length) setDashboardYear(merged[merged.length - 1]);
        else setDashboardYear(currentYear);
      } catch (e) {
        console.warn('[Dashboard] listarAnosDisponiveisVendas (seguindo):', e);
        const currentYear = new Date().getFullYear();
        if (ativo) {
          setYears([currentYear]);
          setDashboardYear(currentYear);
        }
      } finally {
        if (ativo) setYearsLoading(false);
      }
    }

    carregarAnos();
    return () => {
      ativo = false;
    };
  }, [usuario?.uid]);

  /* =========================================================
     ✅ Carrega dados do ANO e do FUNIL
  ========================================================= */
  useEffect(() => {
    let ativo = true;

    async function carregarDashboardAno() {
      if (!usuario?.uid) {
        setDashLoading(false);
        return;
      }

      setDashLoading(true);
      setYearLoading(true);
      setDashError(null);

      const uid = usuario.uid;
      const errors = [];

      const safe = async (label, fn, fallback) => {
        try {
          return await fn();
        } catch (err) {
          console.error(`[Dashboard] Erro em ${label}:`, err);
          errors.push(label);
          return fallback;
        }
      };

      const funilPeriod = makePeriodFromFunnel(periodMode, dashboardYear);
      const anoPeriod = makeYearPeriod(dashboardYear);
      const scoreWindow12m = makeScoreWindowPeriod12m();

      const [
        anunciosAtivosDisponiveis,
        imoveisComPlacaDisponiveis,
        negociosPorStatusTotal,

        negociosPorStatusPeriodo,

        visitasAno,

        vendasMensalAnoRaw,
        vendasRecentesAno,

        movimentoMensalAno,

        // score 12m (legacy) — ainda usado em alguns gráficos/insights
        negociosPorStatus12m,
        visitas12m,
        vendas12mArr,
      ] = await Promise.all([
        safe('contarAnunciosAtivos', () => contarAnunciosAtivos(uid), 0),
        safe('contarImoveisComPlaca', () => contarImoveisComPlaca(uid), 0),

        safe(
          'contarNegociosPorStatus(total)',
          () => contarNegociosPorStatus(uid),
          {}
        ),

        safe(
          'contarNegociosPorStatus(periodo)',
          () => contarNegociosPorStatus(uid, { period: funilPeriod }),
          {}
        ),

        safe(
          'contarVisitasRealizadas(ano)',
          () => contarVisitasRealizadas(uid, { period: anoPeriod }),
          0
        ),

        safe(
          'listarVendasPorMes(ano)',
          () =>
            listarVendasPorMes(uid, {
              mode: 'custom',
              from: anoPeriod.de.toDate(),
              to: anoPeriod.ate.toDate(),
            }),
          []
        ),

        safe(
          'listarVendasRecentes(ano)',
          () => listarVendasRecentes(uid, { limit: 50, period: anoPeriod }),
          []
        ),

        safe(
          'listarMovimentoPorMes(ano)',
          () => listarMovimentoPorMes(uid, { year: dashboardYear }),
          []
        ),

        safe(
          'contarNegociosPorStatus(12m)',
          () => contarNegociosPorStatus(uid, { period: scoreWindow12m }),
          {}
        ),

        safe(
          'contarVisitasRealizadas(12m)',
          () => contarVisitasRealizadas(uid, { period: scoreWindow12m }),
          0
        ),

        safe(
          'listarVendasPorMes(12m)',
          () =>
            listarVendasPorMes(uid, {
              mode: 'custom',
              from: scoreWindow12m.de.toDate(),
              to: scoreWindow12m.ate.toDate(),
            }),
          []
        ),
      ]);

      if (!ativo) return;

      if (errors.length)
        setDashError(`Falha ao carregar: ${errors.join(', ')}`);

      // ✅ total negócios ALL TIME
      const totalNegocios = Object.values(negociosPorStatusTotal || {}).reduce(
        (acc, curr) => acc + (Number(curr) || 0),
        0
      );

      // ✅ financeiro ano
      const vendasMensalAno = Array.isArray(vendasMensalAnoRaw)
        ? vendasMensalAnoRaw
        : [];

      const contratosAno = vendasMensalAno.reduce(
        (acc, m) => acc + (Number(m?.quantidade) || 0),
        0
      );

      const vgvAno = vendasMensalAno.reduce(
        (acc, m) => acc + (Number(m?.vgv) || 0),
        0
      );

      const honorariosAno = vendasMensalAno.reduce(
        (acc, m) => acc + (Number(m?.comissao) || 0),
        0
      );

      const honorariosMeses = vendasMensalAno
        .slice(-3)
        .map((m) => Number(m?.comissao) || 0);

      // tendência (últimos 2 meses do ano selecionado)
      const last = vendasMensalAno[vendasMensalAno.length - 1];
      const prev = vendasMensalAno[vendasMensalAno.length - 2];
      const lastVal = Number(last?.vgv ?? 0) || 0;
      const prevVal = Number(prev?.vgv ?? 0) || 0;

      let tendenciaVgv = null;
      if (last || prev) {
        const delta = lastVal - prevVal;
        let direction = 'flat';
        if (delta > 0) direction = 'up';
        if (delta < 0) direction = 'down';

        const base = prevVal === 0 ? null : prevVal;
        const percent =
          base === null
            ? lastVal > 0
              ? 100
              : 0
            : Math.abs((delta / base) * 100);

        tendenciaVgv = { direction, percent: Number(percent.toFixed(1)) };
      }

      // ✅ score 12m (legacy/fallback)
      const score12mNegociosTotal = Object.values(
        negociosPorStatus12m || {}
      ).reduce((acc, curr) => acc + (Number(curr) || 0), 0);

      const mensal12m = Array.isArray(vendas12mArr) ? vendas12mArr : [];
      const score12mFechamentos = mensal12m.reduce(
        (acc, m) => acc + (Number(m?.quantidade) || 0),
        0
      );

      const score12mVisitas = Number(visitas12m || 0);

      setDashData((prevState) => ({
        ...prevState,

        anunciosAtivos: anunciosAtivosDisponiveis,
        imoveisComPlaca: imoveisComPlacaDisponiveis,
        totalNegocios,

        negociosPorStatusPeriodo: negociosPorStatusPeriodo || {},

        visitasAno: Number(visitasAno || 0),
        contratosAno,
        vgvAno,
        honorariosAno,
        faturamentoAno: honorariosAno,
        tendenciaVgv,
        vendasMensalAno,
        vendasRecentesAno: Array.isArray(vendasRecentesAno)
          ? vendasRecentesAno
          : [],
        honorariosMeses,

        movimentoMensalAno: Array.isArray(movimentoMensalAno)
          ? movimentoMensalAno
          : [],

        score12mNegociosTotal,
        score12mVisitas,
        score12mFechamentos,
      }));

      setDashLoading(false);
      setYearLoading(false);
    }

    carregarDashboardAno();
    return () => {
      ativo = false;
    };
  }, [usuario?.uid, periodMode, dashboardYear]);

  /* =========================================================
     ✅ Carrega snapshot do SCORE (doc /score/{uid})
  ========================================================= */
  useEffect(() => {
    let ativo = true;

    async function carregarScore() {
      if (!usuario?.uid) {
        setScoreLoading(false);
        return;
      }

      setScoreLoading(true);
      setScoreError(null);

      try {
        const snap = await getScoreSnapshot(usuario.uid);
        if (!ativo) return;
        setScoreData(snap || null);
      } catch (err) {
        console.error('[Score] erro ao carregar snapshot:', err);
        if (!ativo) return;
        setScoreError(err?.message || 'Erro ao carregar score.');
      } finally {
        if (ativo) setScoreLoading(false);
      }
    }

    carregarScore();
    return () => {
      ativo = false;
    };
  }, [usuario?.uid]);

  if (!usuario?.uid) {
    return (
      <div className="relative flex-1 bg-[#071426] text-white">
        <div className="w-full p-6">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur">
            <div className="mb-2 text-lg font-semibold">
              Faça login para ver seu painel
            </div>
            <div className="text-sm text-white/70">
              Conecte-se para carregar seus dados.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const diagnostics = [
    ['HeroResultado', HeroResultado],
    ['InsightPanel', InsightPanel],
    ['IndicadorCard', IndicadorCard],
    ['FunilDeVendas', FunilDeVendas],
    ['GraficoMovimento12m', GraficoMovimento12m],
    ['GraficoFinanceiro12m', GraficoFinanceiro12m],
    ['TabelaVendas', TabelaVendas],
    ['ScoreRadialPerformance', ScoreRadialPerformance],
  ].map(([name, Comp]) => ({
    name,
    type: typeof Comp,
    valid: isProbablyComponent(Comp),
  }));

  const periodoLabel = labelPeriodo(periodMode);

  /* =========================================================
     ✅ KPIs topo (SEM visualizações)
     Fonte: dashData.movimentoMensalAno (12 meses)
  ========================================================= */
  const mov = dashData.movimentoMensalAno || [];
  const idxMes = pickMonthIndexForYear(dashboardYear, 12);

  const sImoveis = seriesFromMov(mov, 'imoveis');
  const sPlacas = seriesFromMov(mov, 'placas');
  const sNegocios = seriesFromMov(mov, 'negocios');
  const sVisitas = seriesFromMov(mov, 'visitas');
  const sFechados = seriesFromMov(mov, 'vendas');

  // deltas (mês atual vs anterior)
  const dImoveis = deltaVsPrev(sImoveis, idxMes);
  const dPlacas = deltaVsPrev(sPlacas, idxMes);
  const dNegocios = deltaVsPrev(sNegocios, idxMes);
  const dVisitas = deltaVsPrev(sVisitas, idxMes);
  const dFechados = deltaVsPrev(sFechados, idxMes);

  const kpis = [
    {
      title: 'Imóveis disponíveis',
      total: dashData.anunciosAtivos || 0,
      monthValue: sImoveis[idxMes] || 0,
      delta: dImoveis,
      icon: BarChart2,
      tone: 'success',
    },
    {
      title: 'Com placa',
      total: dashData.imoveisComPlaca || 0,
      monthValue: sPlacas[idxMes] || 0,
      delta: dPlacas,
      icon: MapPin,
      tone: 'brand',
    },
    {
      title: 'Negócios cadastrados',
      total: sumSeries(sNegocios),
      monthValue: sNegocios[idxMes] || 0,
      delta: dNegocios,
      icon: Briefcase,
      tone: 'warning',
    },
    {
      title: 'Visitas realizadas',
      total: sumSeries(sVisitas),
      monthValue: sVisitas[idxMes] || 0,
      delta: dVisitas,
      icon: CalendarCheck,
      tone: 'info',
    },
    {
      title: 'Fechados',
      total: sumSeries(sFechados),
      monthValue: sFechados[idxMes] || 0,
      delta: dFechados,
      icon: CheckCircle2,
      tone: 'success',
    },
  ];

  /* =========================================================
     ✅ SCORE snapshot (TOTAL) — 6K friendly
     - mantém compatibilidade com score antigo
     - lê tanto "pilares6k" (teu scoreEngine) quanto "pilares"
  ========================================================= */

  // ✅ maxTotal do snapshot (se existir) OU fallback 6000/1000
  const totalMax =
    numberOr0(scoreData?.maxTotal) ||
    numberOr0(scoreData?.totalMax) ||
    numberOr0(scoreData?.SCORE_MAX_TOTAL) ||
    6000;

  // ✅ score total do snapshot (tenta várias chaves)
  const scoreTotal =
    numberOr0(scoreData?.scoreTotal) ||
    numberOr0(scoreData?.total6k) ||
    numberOr0(scoreData?.total) ||
    numberOr0(scoreData?.score) ||
    numberOr0(scoreData?.kpiTotalFinal) ||
    0;

  // ✅ delta do “mês” (movimentação exibida, NÃO soma no total)
  const deltaPts =
    numberOr0(scoreData?.movimentacaoMes?.total) ||
    numberOr0(scoreData?.movimentacaoPeriodo?.total) ||
    numberOr0(scoreData?.movimentacao?.total) ||
    numberOr0(scoreData?.elion?.total) ||
    numberOr0(scoreData?.depreciacao?.total) ||
    0;

  const renda12m =
    numberOr0(scoreData?.renda?.renda12m) ||
    numberOr0(scoreData?.renda12m) ||
    numberOr0(scoreData?.renda?.valor12m) ||
    numberOr0(scoreData?.renda?.total12m) ||
    0;

  const nivel = getNivelByRenda12m(renda12m);

  /* ============================================================
     ✅ PILARES 6K (6x1000)
     Fonte preferida:
      - scoreData.pilares6k (teu scoreEngine)
      - fallback: scoreData.pilares
     Fallback inteligente:
      - usa movimentoMensalAno (mês atual) e comissão do mês
============================================================ */

  // ✅ máximos (padrão 1000 cada; pode vir do snapshot)
  const imoveisMax =
    numberOr0(scoreData?.pilares6k?.imoveis?.max) ||
    numberOr0(scoreData?.pilares?.imoveis?.max) ||
    1000;

  const negociosMax =
    numberOr0(scoreData?.pilares6k?.negocios?.max) ||
    numberOr0(scoreData?.pilares?.negocios?.max) ||
    1000;

  const placasMax =
    numberOr0(scoreData?.pilares6k?.placas?.max) ||
    numberOr0(scoreData?.pilares?.placas?.max) ||
    1000;

  const visitasMax =
    numberOr0(scoreData?.pilares6k?.visitas?.max) ||
    numberOr0(scoreData?.pilares?.visitas?.max) ||
    1000;

  const comissaoMax =
    numberOr0(scoreData?.pilares6k?.comissao?.max) ||
    numberOr0(scoreData?.pilares?.comissao?.max) ||
    1000;

  const elionMax =
    numberOr0(scoreData?.pilares6k?.elion?.max) ||
    numberOr0(scoreData?.pilares?.elion?.max) ||
    1000;

  // ✅ pontos por pilar — tenta snapshot primeiro
  const snapImoveisPts =
    numberOr0(scoreData?.pilares6k?.imoveis?.pts) ||
    numberOr0(scoreData?.pilares?.imoveis?.pts) ||
    numberOr0(scoreData?.comportamento?.imoveisPts) ||
    0;

  const snapNegociosPts =
    numberOr0(scoreData?.pilares6k?.negocios?.pts) ||
    numberOr0(scoreData?.pilares?.negocios?.pts) ||
    numberOr0(scoreData?.comportamento?.negociosPts) ||
    0;

  const snapPlacasPts =
    numberOr0(scoreData?.pilares6k?.placas?.pts) ||
    numberOr0(scoreData?.pilares?.placas?.pts) ||
    numberOr0(scoreData?.comportamento?.placasPts) ||
    0;

  const snapVisitasPts =
    numberOr0(scoreData?.pilares6k?.visitas?.pts) ||
    numberOr0(scoreData?.pilares?.visitas?.pts) ||
    numberOr0(scoreData?.comportamento?.visitasPts) ||
    0;

  const snapComissaoPts =
    numberOr0(scoreData?.pilares6k?.comissao?.pts) ||
    numberOr0(scoreData?.pilares?.comissao?.pts) ||
    numberOr0(scoreData?.renda?.pts) ||
    0;

  // “Elion” (penitência/bônus) pode ser negativo/positivo
  const snapElionPts =
    numberOr0(scoreData?.pilares6k?.elion?.pts) ||
    numberOr0(scoreData?.pilares?.elion?.pts) ||
    numberOr0(scoreData?.elion?.total) ||
    numberOr0(scoreData?.elionPts) ||
    0;

  const hasAnyPilarFromSnapshot =
    snapImoveisPts ||
    snapNegociosPts ||
    snapPlacasPts ||
    snapVisitasPts ||
    snapComissaoPts ||
    snapElionPts;

  // ✅ fallback inteligente por mês (até você finalizar engine everywhere)
  const monthImoveis = numberOr0(sImoveis[idxMes]);
  const monthNegocios = numberOr0(sNegocios[idxMes]);
  const monthPlacas = numberOr0(sPlacas[idxMes]);
  const monthVisitas = numberOr0(sVisitas[idxMes]);

  const TARGETS_MES = {
    imoveis: 8,
    negocios: 12,
    placas: 6,
    visitas: 20,
    comissao: 50000, // R$ 50k/mês (ajusta)
    elion: 120, // placeholder
  };

  const comissaoMes = (() => {
    const vendasAno = Array.isArray(dashData.vendasMensalAno)
      ? dashData.vendasMensalAno
      : [];
    const row = vendasAno[idxMes];
    return numberOr0(row?.comissao);
  })();

  const fallbackImoveisPts = clamp(
    (monthImoveis / TARGETS_MES.imoveis) * imoveisMax,
    0,
    imoveisMax
  );

  const fallbackNegociosPts = clamp(
    (monthNegocios / TARGETS_MES.negocios) * negociosMax,
    0,
    negociosMax
  );

  const fallbackPlacasPts = clamp(
    (monthPlacas / TARGETS_MES.placas) * placasMax,
    0,
    placasMax
  );

  const fallbackVisitasPts = clamp(
    (monthVisitas / TARGETS_MES.visitas) * visitasMax,
    0,
    visitasMax
  );

  const fallbackComissaoPts = clamp(
    (comissaoMes / TARGETS_MES.comissao) * comissaoMax,
    0,
    comissaoMax
  );

  const fallbackElionPts = 0;

  const pImoveisPts = hasAnyPilarFromSnapshot
    ? snapImoveisPts
    : fallbackImoveisPts;
  const pNegociosPts = hasAnyPilarFromSnapshot
    ? snapNegociosPts
    : fallbackNegociosPts;
  const pPlacasPts = hasAnyPilarFromSnapshot
    ? snapPlacasPts
    : fallbackPlacasPts;
  const pVisitasPts = hasAnyPilarFromSnapshot
    ? snapVisitasPts
    : fallbackVisitasPts;
  const pComissaoPts = hasAnyPilarFromSnapshot
    ? snapComissaoPts
    : fallbackComissaoPts;
  const pElionPts = hasAnyPilarFromSnapshot ? snapElionPts : fallbackElionPts;

  // ✅ opções de ano
  const yearOptions = useMemo(() => {
    const list =
      Array.isArray(years) && years.length ? years : [new Date().getFullYear()];
    return list;
  }, [years]);

  // ✅ Select do ANO (topo)
  const dashboardYearSelect = (
    <div className="flex items-center gap-2">
      <div className="text-xs text-white/50">
        {yearsLoading ? 'Carregando…' : 'Ano'}
      </div>

      <select
        value={dashboardYear}
        onChange={(e) => setDashboardYear(Number(e.target.value))}
        disabled={yearsLoading}
        className="appearance-none rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 focus:outline-none disabled:opacity-60"
        title="Selecione o ano da dashboard"
      >
        {yearOptions.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-[#071426]">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#071426] via-[#071a33] to-[#040a14]" />
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-[90px]" />
        <div className="absolute -bottom-48 -right-48 h-[620px] w-[620px] rounded-full bg-sky-500/12 blur-[110px]" />
        <div className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(#ffffff_1px,transparent_1px)] [background-size:22px_22px]" />
      </div>

      <div className="relative z-10 h-full w-full overflow-y-auto no-scrollbar">
        <div className="mx-auto w-full max-w-7xl space-y-4 px-6 py-6 lg:px-8">
          {SHOW_DIAGNOSTICS && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-xs text-white/70 backdrop-blur">
              <div className="mb-1 font-medium text-white">Diagnóstico</div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {diagnostics.map((d) => (
                  <div
                    key={d.name}
                    className={`rounded-xl border p-2 ${
                      d.valid
                        ? 'border-emerald-300/20 bg-emerald-500/10'
                        : 'border-amber-300/20 bg-amber-500/10'
                    }`}
                  >
                    <div className="font-medium text-white">{d.name}</div>
                    <div className="opacity-80">typeof: {d.type}</div>
                    <div
                      className={`text-[11px] ${
                        d.valid ? 'text-emerald-200' : 'text-amber-200'
                      }`}
                    >
                      {d.valid ? 'OK' : 'Inválido'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dashError && (
            <div className="rounded-2xl border border-amber-300/20 bg-amber-500/10 p-3 text-xs text-amber-200 backdrop-blur">
              <div className="font-semibold">Problema ao carregar o painel</div>
              <div className="mt-1 opacity-80">{dashError}</div>
            </div>
          )}

          {scoreError && (
            <div className="rounded-2xl border border-amber-300/20 bg-amber-500/10 p-3 text-xs text-amber-200 backdrop-blur">
              <div className="font-semibold">Problema ao carregar o score</div>
              <div className="mt-1 opacity-80">{scoreError}</div>
            </div>
          )}

          <DashboardHeader
            subtitle={`Bem-vindo de volta, ${usuario?.nome || 'Elion Partner'}.`}
            yearSlot={dashboardYearSelect}
            onNovaVenda={() => {}}
          />

          {/* ✅ KPIs (SEM visualizações) */}
          {dashLoading ? (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-[82px] animate-pulse rounded-2xl border border-white/10 bg-white/5 backdrop-blur"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {kpis.map((k) => (
                <IndicadorCard
                  key={k.title}
                  title={k.title}
                  total={k.total}
                  monthValue={k.monthValue}
                  delta={k.delta}
                  icon={k.icon}
                  tone={k.tone}
                  compact
                />
              ))}
            </div>
          )}

          {/* Linha Principal */}
          <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-12">
            <div className="h-full lg:col-span-7">
              <SafeBlock
                label="HeroResultado"
                Comp={HeroResultado}
                loading={dashLoading || yearLoading}
                vgvTotal={dashData.vgvAno}
                honorariosTotal={dashData.honorariosAno}
                faturamentoTotal={dashData.faturamentoAno}
                tendenciaVgv={dashData.tendenciaVgv}
                compact
                dark
              />
            </div>

            <div className="h-full lg:col-span-5 lg:row-span-2">
              <ErrorBoundary label="ScoreRadialPerformance">
                <ScoreRadialPerformance
                  loading={dashLoading || scoreLoading}
                  totalMax={totalMax}
                  scoreTotal={scoreTotal}
                  deltaPts={deltaPts}
                  renda12m={renda12m}
                  nivelLabel={nivel?.label || 'Iniciante'}
                  nivelFaixa={nivel?.faixa || 'até R$ 59 mil'}
                  // ✅ 6K pillars
                  imoveisPts={pImoveisPts}
                  negociosPts={pNegociosPts}
                  placasPts={pPlacasPts}
                  visitasPts={pVisitasPts}
                  comissaoPts={pComissaoPts}
                  elionPts={pElionPts}
                  imoveisMax={imoveisMax}
                  negociosMax={negociosMax}
                  placasMax={placasMax}
                  visitasMax={visitasMax}
                  comissaoMax={comissaoMax}
                  elionMax={elionMax}
                  // ✅ regras (placeholders; depois a gente liga no engine)
                  regraPenitenciaLabel="Sem movimento no mês"
                  regraPenitenciaPts="-100 pts"
                  regraBonusLabel="Full-player no mês"
                  regraBonusPts="+85 pts"
                  regraJanelaLabel="Janela móvel"
                  regraJanelaValue="12 meses"
                />
              </ErrorBoundary>
            </div>

            <div className="h-full lg:col-span-7">
              <SafeBlock
                label="FunilDeVendas"
                Comp={FunilDeVendas}
                loading={dashLoading}
                totalNegocios={Object.values(
                  dashData.negociosPorStatusPeriodo || {}
                ).reduce((acc, curr) => acc + (Number(curr) || 0), 0)}
                totalContratos={dashData.contratosAno}
                visitasRealizadas={dashData.visitasAno}
                periodoInicial={periodMode}
                onPeriodoChange={setPeriodMode}
                compact
                dark
              />
            </div>
          </div>

          {/* ✅ InsightPanel */}
          <SafeBlock
            label="InsightPanel"
            Comp={InsightPanel}
            loading={dashLoading || yearLoading}
            negociosPorStatus={dashData.negociosPorStatusPeriodo}
            vendasRecentes={dashData.vendasRecentesAno}
            visitasMes={dashData.visitasAno}
            imoveisAtivos={dashData.anunciosAtivos}
            honorariosMeses={dashData.honorariosMeses}
            movimentoMensal={dashData.movimentoMensalAno}
            year={dashboardYear}
            compact
            dark
          />

          {/* ✅ Header dos gráficos do ano */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-white/80">
              Histórico anual — {dashboardYear}{' '}
              <span className="text-white/40">({periodoLabel} no funil)</span>
            </div>
            {dashboardYearSelect}
          </div>

          {/* ✅ GRÁFICOS */}
          <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-12">
            <div className="h-full lg:col-span-6">
              <SafeBlock
                label="GraficoMovimento12m"
                Comp={GraficoMovimento12m}
                loading={yearLoading}
                dataMensal={dashData.movimentoMensalAno}
                year={dashboardYear}
                rightSlot={dashboardYearSelect}
                compact
                dark
              />
            </div>

            <div className="h-full lg:col-span-6">
              <SafeBlock
                label="GraficoFinanceiro12m"
                Comp={GraficoFinanceiro12m}
                loading={yearLoading}
                dataMensal={dashData.vendasMensalAno}
                vgvTotal={dashData.vgvAno}
                honorariosTotal={dashData.honorariosAno}
                faturamentoTotal={dashData.faturamentoAno}
                year={dashboardYear}
                rightSlot={dashboardYearSelect}
                compact
                dark
              />
            </div>
          </div>

          <SafeBlock
            label="TabelaVendas"
            Comp={TabelaVendas}
            loading={yearLoading}
            vendas={dashData.vendasRecentesAno}
            year={dashboardYear}
            rightSlot={dashboardYearSelect}
            onSaved={() => {}}
            compact
            dark
          />
        </div>
      </div>
    </div>
  );
}
